import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  PrinterWithRelations,
  PrinterLibraryQuery,
  CreatePrinterLibraryInput,
  UpdatePrinterLibraryInput,
  TenantPrinterWithDetails,
  TenantPrinterQuery,
  AddPrinterToLibraryInput,
  UpdateTenantPrinterInput,
  PrinterBrand,
  PrinterModel,
  AccessoryType,
  CreatePrinterBrandInput,
  CreatePrinterModelInput,
  CreateAccessoryTypeInput,
  TenantPrinterAccessoryWithDetails,
  AddAccessoryToPrinterInput,
  UpdateAccessoryInput,
  TenantPrinterNote,
  CreatePrinterNoteInput,
  UpdatePrinterNoteInput,
  PrintWithRelations,
  PrintQuery,
  CreatePrintInput,
  UpdatePrintInput,
} from '@maker-toolchest/shared';
import {
  printerLibraryApi,
  tenantPrinterApi,
  printerLookupApi,
  printApi,
  type PaginatedResult,
} from './api';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const printerKeys = {
  // Library
  library: ['printers', 'library'] as const,
  librarySearch: (query?: PrinterLibraryQuery) => [...printerKeys.library, 'search', query] as const,
  libraryDetail: (id: string) => [...printerKeys.library, id] as const,

  // Tenant Printers
  all: ['printers'] as const,
  lists: () => [...printerKeys.all, 'list'] as const,
  list: (query?: TenantPrinterQuery) => [...printerKeys.lists(), query] as const,
  details: () => [...printerKeys.all, 'detail'] as const,
  detail: (id: string) => [...printerKeys.details(), id] as const,

  // Nested: Accessories
  accessories: (printerId: string) => [...printerKeys.detail(printerId), 'accessories'] as const,

  // Nested: Notes
  notes: (printerId: string) => [...printerKeys.detail(printerId), 'notes'] as const,

  // Lookups (cached longer - 5 minutes)
  brands: ['printer-brands'] as const,
  models: (brandId?: string) => ['printer-models', brandId] as const,
  accessoryTypes: ['accessory-types'] as const,

  // Prints
  prints: ['prints'] as const,
  printsList: (query?: PrintQuery) => [...printerKeys.prints, 'list', query] as const,
  printDetail: (id: string) => [...printerKeys.prints, id] as const,
};

// ============================================================================
// PRINTER LIBRARY HOOKS
// ============================================================================

export function usePrinterLibrary(
  query?: PrinterLibraryQuery,
  options?: Omit<UseQueryOptions<PaginatedResult<PrinterWithRelations>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.librarySearch(query),
    queryFn: () => printerLibraryApi.search(query),
    placeholderData: (previousData) => previousData,
    ...options,
  });
}

export function usePrinterLibraryItem(
  id: string,
  options?: Omit<UseQueryOptions<PrinterWithRelations>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.libraryDetail(id),
    queryFn: () => printerLibraryApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreatePrinterLibrary(
  options?: UseMutationOptions<PrinterWithRelations, Error, CreatePrinterLibraryInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLibraryApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.library });
    },
    ...options,
  });
}

export function useUpdatePrinterLibrary(
  options?: UseMutationOptions<
    PrinterWithRelations,
    Error,
    { id: string; input: UpdatePrinterLibraryInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) => printerLibraryApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.libraryDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: printerKeys.library });
    },
    ...options,
  });
}

export function useDeletePrinterLibrary(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLibraryApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.library });
    },
    ...options,
  });
}

// ============================================================================
// TENANT PRINTER HOOKS
// ============================================================================

export function useTenantPrinters(
  query?: TenantPrinterQuery,
  options?: Omit<UseQueryOptions<TenantPrinterWithDetails[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.list(query),
    queryFn: () => tenantPrinterApi.list(query),
    ...options,
  });
}

export function useTenantPrinter(
  id: string,
  options?: Omit<UseQueryOptions<TenantPrinterWithDetails>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.detail(id),
    queryFn: () => tenantPrinterApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

export function useAddPrinterToLibrary(
  options?: UseMutationOptions<TenantPrinterWithDetails, Error, AddPrinterToLibraryInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: tenantPrinterApi.addToLibrary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.lists() });
    },
    ...options,
  });
}

export function useUpdateTenantPrinter(
  options?: UseMutationOptions<
    TenantPrinterWithDetails,
    Error,
    { id: string; input: UpdateTenantPrinterInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) => tenantPrinterApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: printerKeys.lists() });
    },
    ...options,
  });
}

export function useRemovePrinterFromLibrary(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: tenantPrinterApi.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.lists() });
    },
    ...options,
  });
}

// ============================================================================
// PRINTER ACCESSORIES HOOKS
// ============================================================================

export function usePrinterAccessories(
  printerId: string,
  options?: Omit<UseQueryOptions<TenantPrinterAccessoryWithDetails[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.accessories(printerId),
    queryFn: () => tenantPrinterApi.listAccessories(printerId),
    enabled: !!printerId,
    ...options,
  });
}

export function useAddAccessory(
  options?: UseMutationOptions<
    TenantPrinterAccessoryWithDetails,
    Error,
    { printerId: string; input: AddAccessoryToPrinterInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ printerId, input }) => tenantPrinterApi.addAccessory(printerId, input),
    onSuccess: (_, variables) => {
      // Invalidate accessories list
      queryClient.invalidateQueries({ queryKey: printerKeys.accessories(variables.printerId) });
      // Invalidate printer detail (slot counts changed)
      queryClient.invalidateQueries({ queryKey: printerKeys.detail(variables.printerId) });
    },
    ...options,
  });
}

export function useUpdateAccessory(
  options?: UseMutationOptions<
    TenantPrinterAccessoryWithDetails,
    Error,
    { printerId: string; accessoryId: string; input: UpdateAccessoryInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ printerId, accessoryId, input }) =>
      tenantPrinterApi.updateAccessory(printerId, accessoryId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.accessories(variables.printerId) });
      // If quantity changed, slot counts changed
      queryClient.invalidateQueries({ queryKey: printerKeys.detail(variables.printerId) });
    },
    ...options,
  });
}

export function useRemoveAccessory(
  options?: UseMutationOptions<void, Error, { printerId: string; accessoryId: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ printerId, accessoryId }) =>
      tenantPrinterApi.removeAccessory(printerId, accessoryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.accessories(variables.printerId) });
      // Slot counts changed
      queryClient.invalidateQueries({ queryKey: printerKeys.detail(variables.printerId) });
    },
    ...options,
  });
}

// ============================================================================
// PRINTER NOTES HOOKS
// ============================================================================

export function usePrinterNotes(
  printerId: string,
  options?: Omit<UseQueryOptions<TenantPrinterNote[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.notes(printerId),
    queryFn: () => tenantPrinterApi.listNotes(printerId),
    enabled: !!printerId,
    ...options,
  });
}

export function useCreatePrinterNote(
  options?: UseMutationOptions<
    TenantPrinterNote,
    Error,
    { printerId: string; input: CreatePrinterNoteInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ printerId, input }) => tenantPrinterApi.createNote(printerId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.notes(variables.printerId) });
      // Note count changed
      queryClient.invalidateQueries({ queryKey: printerKeys.detail(variables.printerId) });
    },
    ...options,
  });
}

export function useUpdatePrinterNote(
  options?: UseMutationOptions<
    TenantPrinterNote,
    Error,
    { printerId: string; noteId: string; input: UpdatePrinterNoteInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ printerId, noteId, input }) =>
      tenantPrinterApi.updateNote(printerId, noteId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.notes(variables.printerId) });
    },
    ...options,
  });
}

export function useDeletePrinterNote(
  options?: UseMutationOptions<void, Error, { printerId: string; noteId: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ printerId, noteId }) => tenantPrinterApi.deleteNote(printerId, noteId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.notes(variables.printerId) });
      // Note count changed
      queryClient.invalidateQueries({ queryKey: printerKeys.detail(variables.printerId) });
    },
    ...options,
  });
}

// ============================================================================
// LOOKUP HOOKS (Cached longer)
// ============================================================================

export function usePrinterBrands(
  options?: Omit<UseQueryOptions<PrinterBrand[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.brands,
    queryFn: printerLookupApi.listBrands,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useCreatePrinterBrand(
  options?: UseMutationOptions<PrinterBrand, Error, CreatePrinterBrandInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLookupApi.createBrand,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.brands });
    },
    ...options,
  });
}

export function useDeletePrinterBrand(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLookupApi.deleteBrand,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.brands });
    },
    ...options,
  });
}

export function usePrinterModels(
  brandId?: string,
  options?: Omit<UseQueryOptions<PrinterModel[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.models(brandId),
    queryFn: () => printerLookupApi.listModels(brandId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useCreatePrinterModel(
  options?: UseMutationOptions<PrinterModel, Error, CreatePrinterModelInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLookupApi.createModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printer-models'] });
    },
    ...options,
  });
}

export function useDeletePrinterModel(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLookupApi.deleteModel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printer-models'] });
    },
    ...options,
  });
}

export function useAccessoryTypes(
  options?: Omit<UseQueryOptions<AccessoryType[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.accessoryTypes,
    queryFn: printerLookupApi.listAccessoryTypes,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useCreateAccessoryType(
  options?: UseMutationOptions<AccessoryType, Error, CreateAccessoryTypeInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLookupApi.createAccessoryType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.accessoryTypes });
    },
    ...options,
  });
}

export function useDeleteAccessoryType(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printerLookupApi.deleteAccessoryType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.accessoryTypes });
    },
    ...options,
  });
}

// ============================================================================
// PRINT HOOKS
// ============================================================================

export function usePrints(
  query?: PrintQuery,
  options?: Omit<UseQueryOptions<PrintWithRelations[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.printsList(query),
    queryFn: () => printApi.list(query),
    ...options,
  });
}

export function usePrint(
  id: string,
  options?: Omit<UseQueryOptions<PrintWithRelations>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: printerKeys.printDetail(id),
    queryFn: () => printApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreatePrint(
  options?: UseMutationOptions<PrintWithRelations, Error, CreatePrintInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printApi.create,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.prints });
      // Stats/usage changed for printer
      queryClient.invalidateQueries({ queryKey: printerKeys.detail(variables.tenantPrinterId) });
    },
    ...options,
  });
}

export function useUpdatePrint(
  options?: UseMutationOptions<
    PrintWithRelations,
    Error,
    { id: string; input: UpdatePrintInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) => printApi.update(id, input),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: printerKeys.printDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: printerKeys.prints });
      // If completed, stats changed
      if (data.status === 'COMPLETED') {
        queryClient.invalidateQueries({ queryKey: printerKeys.detail(data.tenantPrinterId) });
      }
    },
    ...options,
  });
}

export function useDeletePrint(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: printApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.prints });
      // Stats may have changed for associated printer
      queryClient.invalidateQueries({ queryKey: printerKeys.all });
    },
    ...options,
  });
}

// Missing note hooks - add aliases to print hooks for now
export const useCreateNote = useCreatePrinterNote;
export const useUpdateNote = useUpdatePrinterNote;
export const useDeleteNote = useDeletePrinterNote;

// Legacy exports for backward compatibility
export const usePrinters = useTenantPrinters;
export const usePrinter = useTenantPrinter;

// === Convenience Hooks ===

/**
 * Combined hook for all printer lookups (brands, models, accessory types)
 */
export function usePrinterLookups() {
  const { data: brands = [], isLoading: brandsLoading } = usePrinterBrands();
  const { data: models = [], isLoading: modelsLoading } = usePrinterModels();
  const { data: accessoryTypes = [], isLoading: typesLoading } = useAccessoryTypes();

  return {
    brands,
    models,
    accessoryTypes,
    isLoading: brandsLoading || modelsLoading || typesLoading,
  };
}

// useStorageLocations has been removed - import from '@/features/storage' instead
